import { useState, useEffect, useRef, useMemo, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarWrapper, LogoWrapper, SidebarContent, UserSection, UserAvatar, UserInfo, UserMenu, UserMenuHeader, UserMenuName, UserMenuEmail, UserMenuItem, TrialBanner, SidebarDivider } from "./Styles";
import { FiLogOut, FiBell, FiSettings, FiGift } from "react-icons/fi";
import { TbMessages, TbPigMoney } from "react-icons/tb";
import { FaUsers } from "react-icons/fa";
import { AiOutlineFileSearch, AiOutlineFileText } from "react-icons/ai";
import MikeLogo from "../../Assets/logo.png";
import { useAuth } from "../../Common/Hooks/useAuth";
import { useBusiness } from "../../Common/Hooks/useBusiness";
import { getAvatarColor } from "../../Common/Utils/getAvatarColor";
import { eventBus } from "../../Common/EventBus";
import { MainNav } from "./MainNav";
import { CopilotSubSidebar } from "./CopilotSubSidebar";
import { UsersModal } from "./UsersModal";
import { PdfSettingsModal } from "./PdfSettingsModal";
import { ReferralModal } from "./ReferralModal";

import { isAdmin } from "../../Common/Utils/isAdmin";
import { isSubscriptionActive } from "../../Common/Utils/subscriptionUtils";
import { usePushNotifications } from "../../Common/Hooks/usePushNotifications";
import { Notification as AppNotification } from "../Notifications";
import {
  ReportSectionNavDesktop,
  ReportOutline,
  ReportCaseMeta,
  sectionsFromOutline,
  blocksOutlineFromOutline,
} from "../../Pages/DueDiligence/LegalFiscalReport/components/ReportSectionNav";
import { ToggleSwitch } from "./ToggleSwitch";
export const Sidebar = () => {
  const { t } = useTranslation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showPdfSettingsModal, setShowPdfSettingsModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string>("");
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean>(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const { clientUser: client, logout } = useAuth();
  const { business } = useBusiness();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userSectionRef = useRef<HTMLDivElement>(null);
  const isHeldOpenByTourRef = useRef(false);
  const userIsAdmin = isAdmin(business, client);
  const { isSupported: isPushSupported, isSubscribed: isPushSubscribed, permission: pushPermission, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushNotifications();

  const isCopilotActive = location.pathname === "/" || location.pathname.startsWith("/chat");
  const isDueDiligenceActive = location.pathname.startsWith("/due-diligence");
  const isContractsActive = location.pathname.startsWith("/contracts");
  const isReportPage = location.pathname.endsWith("/report");
  // The report always hangs off a case, so the way back out of it is its own path minus the trailing segment.
  const reportCaseDetailPath = isReportPage
    ? location.pathname.slice(0, -"/report".length)
    : null;
  const isRail = isReportPage && reportCaseDetailPath !== null;
  const [activeSectionId, setActiveSectionId] = useState("resumen");
  const [enabledSections, setEnabledSections] = useState<Record<string, boolean>>({});
  const [outline, setOutline] = useState<ReportOutline>([]);
  const [enabledBlocks, setEnabledBlocks] = useState<Record<string, boolean>>({});
  const [caseMeta, setCaseMeta] = useState<ReportCaseMeta | null>(null);

  // The report builds its section list from the dynamic outline (single source
  // of truth lives in the backend).
  const reportSections = sectionsFromOutline(outline);
  const blocksOutline = blocksOutlineFromOutline(outline);

  // Listen for section changes and enabled state from the report page
  useEffect(() => {
    const handleChanged = (sectionId: string) => setActiveSectionId(sectionId);
    const handleEnabled = (sections: Record<string, boolean>) => setEnabledSections(sections);
    const handleOutline = (next: ReportOutline) => setOutline(next);
    const handleEnabledBlocks = (blocks: Record<string, boolean>) => setEnabledBlocks(blocks);
    const handleCaseMeta = (meta: ReportCaseMeta) => setCaseMeta(meta);
    eventBus.on("REPORT_SECTION_CHANGED", handleChanged);
    eventBus.on("REPORT_ENABLED_SECTIONS", handleEnabled);
    eventBus.on("REPORT_OUTLINE", handleOutline);
    eventBus.on("REPORT_ENABLED_BLOCKS", handleEnabledBlocks);
    eventBus.on("REPORT_CASE_META", handleCaseMeta);
    return () => {
      eventBus.off("REPORT_SECTION_CHANGED", handleChanged);
      eventBus.off("REPORT_ENABLED_SECTIONS", handleEnabled);
      eventBus.off("REPORT_OUTLINE", handleOutline);
      eventBus.off("REPORT_ENABLED_BLOCKS", handleEnabledBlocks);
      eventBus.off("REPORT_CASE_META", handleCaseMeta);
    };
  }, []);

  useEffect(() => {
    const openForTour = () => {
      isHeldOpenByTourRef.current = true;
      setShowUserMenu(true);
    };
    const closeForTour = () => {
      isHeldOpenByTourRef.current = false;
      setShowUserMenu(false);
    };

    eventBus.on("OPEN_USER_MENU", openForTour);
    eventBus.on("CLOSE_USER_MENU", closeForTour);

    return () => {
      eventBus.off("OPEN_USER_MENU", openForTour);
      eventBus.off("CLOSE_USER_MENU", closeForTour);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isHeldOpenByTourRef.current) return;
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  useEffect(() => {
    const calculateMenuPosition = () => {
      if (userSectionRef.current && showUserMenu) {
        const rect = userSectionRef.current.getBoundingClientRect();
        // Position menu just above UserSection, aligned with avatar
        // Menu will appear on top of sidebars thanks to high z-index
        setMenuPosition({
          top: rect.top,
          left: rect.left
        });
      } else {
        setMenuPosition(null);
      }
    };

    if (showUserMenu) {
      calculateMenuPosition();
      window.addEventListener('resize', calculateMenuPosition);
      window.addEventListener('scroll', calculateMenuPosition, true);
    }

    return () => {
      window.removeEventListener('resize', calculateMenuPosition);
      window.removeEventListener('scroll', calculateMenuPosition, true);
    };
  }, [showUserMenu]);

  const getUserInitials = () => {
    if (!client) return "U";
    const name = client.name || "";
    const parts = name.split(" ").filter(part => part.trim().length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length > 0) {
      return parts[0][0].toUpperCase();
    }
    return "U";
  };

  const getUserName = () => {
    return client?.name || t("common.sidebar.userMenu.fallbackName");
  };

  useEffect(() => {
    const loadSubscriptionTier = async () => {
      if (!business) {
        setSubscriptionTier("");
        setHasActiveSubscription(false);
        return;
      }

      // Staff-owned businesses always have access
      if (business.isStaffOwned) {
        setHasActiveSubscription(true);
        setSubscriptionTier(t("common.sidebar.subscriptionTier.staff"));
        return;
      }

      // Kept by GET /business, which reads it from our own table, not Stripe
      if (isSubscriptionActive(business.copilotSubscription)) {
        setHasActiveSubscription(true);
        // TODO: Get actual plan when available in API
        setSubscriptionTier(t("common.sidebar.subscriptionTier.active"));
        return;
      }

      // If no active subscription, check if in trial
      setHasActiveSubscription(false);
      if (business.trialEndDate) {
        const trialEndDate = new Date(business.trialEndDate);
        const now = new Date();
        if (trialEndDate > now) {
          setSubscriptionTier(t("common.sidebar.subscriptionTier.trial"));
          return;
        }
      }

      // If not in trial and no active subscription
      setSubscriptionTier("");
    };

    loadSubscriptionTier();


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id, business?.copilotSubscription?.status]);

  const handleNewChat = () => {
    navigate("/chat");
  };

  // One list feeds both shapes of the main nav: the labelled items everywhere
  // else, and the icon rail on the report page. Clicking a rail icon navigates
  // away from the report, which is what brings the labelled items back.
  const mainNavItems = [
    {
      id: "copilot",
      icon: <TbMessages />,
      label: t("common.sidebar.nav.copilot"),
      description: t("common.sidebar.nav.copilotDescription"),
      isActive: isCopilotActive,
      onClick: () => navigate("/chat"),
      badge: undefined as ReactNode,
    },
    {
      id: "dueDiligence",
      icon: <AiOutlineFileSearch />,
      label: t("common.sidebar.nav.dueDiligence"),
      description: t("common.sidebar.nav.dueDiligenceDescription"),
      isActive: isDueDiligenceActive,
      onClick: () => navigate("/due-diligence"),
      badge: undefined as ReactNode,
    },
    {
      id: "contracts",
      icon: <AiOutlineFileText />,
      label: t("common.sidebar.nav.contracts"),
      description: t("common.sidebar.nav.contractsDescription"),
      isActive: isContractsActive,
      onClick: () => navigate("/contracts"),
      badge: (
        <span style={{
          fontSize: '0.55rem',
          fontWeight: 700,
          backgroundColor: '#DBEAFE',
          color: '#1E40AF',
          padding: '1px 4px',
          borderRadius: '3px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          {t("common.sidebar.nav.contractsBadge")}
        </span>
      ) as ReactNode,
    },
  ];

  // Trial banner logic - only shown if NO active subscription
  const trialBannerProps = useMemo(() => {
    // Do not show banner if active subscription or on choose plan page
    if (hasActiveSubscription || location.pathname === '/choose-plan') {
      return null;
    }

    if (!business || !business.trialEndDate) {
      return null;
    }

    const trialEndDate = new Date(business.trialEndDate);
    const now = new Date();

    if (trialEndDate > now) {
      const hoursLeft = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60));
      let message = '';
      if (hoursLeft >= 24) {
        const daysLeft = Math.floor(hoursLeft / 24);
        message = t("common.sidebar.trialBanner.expiresInDays", { count: daysLeft });
      } else {
        message = t("common.sidebar.trialBanner.expiresInHours", { count: hoursLeft });
      }
      return {
        type: 'info' as const,
        message
      };
    } else {
      return {
        type: 'warning' as const,
        message: t("common.sidebar.trialBanner.expired")
      };
    }
  }, [business, location.pathname, hasActiveSubscription, t]);

  const handleTrialBannerClick = () => {
    navigate('/choose-plan');
  };

  const handleNotificationsToggle = async () => {
    if (isPushSubscribed) {
      await pushUnsubscribe();
    } else {
      if (pushPermission === "denied") {
        eventBus.emit(
          "NOTIFICATION",
          AppNotification.buildNotification("info", t("common.sidebar.notifications.enableInBrowserSettings"))
        );
        return;
      }
      await pushSubscribe();
    }
    setShowUserMenu(false);
  };

  return (
    <>
      <SidebarWrapper>
        <LogoWrapper>
          <img src={MikeLogo} alt={t("common.sidebar.logoAlt")} />
        </LogoWrapper>

        <SidebarContent>
          <MainNav
            items={mainNavItems}
            isRail={isRail}
            back={{
              label: t("common.sidebar.nav.backToCase"),
              description: t("common.sidebar.nav.backToCaseDescription"),
              onClick: () => navigate(reportCaseDetailPath ?? "/due-diligence"),
            }}
          />
          {isReportPage && (
            <>
              <SidebarDivider />
              <ReportSectionNavDesktop
                activeSectionId={activeSectionId}
                enabledSections={enabledSections}
                enabledBlocks={enabledBlocks}
                blocksOutline={blocksOutline}
                sections={reportSections}
                caseMeta={caseMeta}
                onSectionClick={(id) => {
                  eventBus.emit("REPORT_SCROLL_TO_SECTION", id);
                }}
                onToggleSection={(id) => {
                  eventBus.emit("REPORT_TOGGLE_SECTION", id);
                }}
                onBlockClick={(id) => {
                  eventBus.emit("REPORT_SCROLL_TO_BLOCK", id);
                }}
                onToggleBlock={(id) => {
                  eventBus.emit("REPORT_TOGGLE_BLOCK", id);
                }}
              />
            </>
          )}
        </SidebarContent>

        {trialBannerProps && (
          <TrialBanner $type={trialBannerProps.type} onClick={handleTrialBannerClick}>
            <div style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
              {trialBannerProps.message}
            </div>
          </TrialBanner>
        )}

        <UserSection ref={userSectionRef} data-tour="user-menu">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
              width: '100%',
              justifyContent: 'flex-start'
            }}
            onClick={() => setShowUserMenu(!showUserMenu)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <UserAvatar
              $bgColor={getAvatarColor(client?.email || "").bg}
              $textColor={getAvatarColor(client?.email || "").text}
            >
              {getUserInitials()}
            </UserAvatar>
            <UserInfo>
              <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#ffffff' }}>
                {getUserName()}
              </div>
              {subscriptionTier && (
                <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                  {subscriptionTier}
                </div>
              )}
            </UserInfo>
          </div>
        </UserSection>

      </SidebarWrapper>

      {showUserMenu && menuPosition && createPortal(
        <UserMenu
          ref={userMenuRef}
          data-tour="user-menu-panel"
          $top={menuPosition.top}
          $left={menuPosition.left}
        >
          <UserMenuHeader>
            <UserMenuName>{client?.name || t("common.sidebar.userMenu.fallbackName")}</UserMenuName>
            <UserMenuEmail>{client?.email || ""}</UserMenuEmail>
          </UserMenuHeader>
          {userIsAdmin && (
            <UserMenuItem data-tour="team" onClick={() => {
              setShowUsersModal(true);
              setShowUserMenu(false);
            }}>
              <FaUsers size={16} />
              <span>{t("common.sidebar.userMenu.team")}</span>
            </UserMenuItem>
          )}
          {userIsAdmin && (
            <UserMenuItem data-tour="subscription" onClick={() => {
              eventBus.emit("OPEN_SUBSCRIPTION_MODAL");
              setShowUserMenu(false);
            }}>
              <TbPigMoney size={16} />
              <span>{t("common.sidebar.userMenu.subscription")}</span>
            </UserMenuItem>
          )}
          <UserMenuItem data-tour="pdf-settings" onClick={() => {
            setShowPdfSettingsModal(true);
            setShowUserMenu(false);
          }}>
            <FiSettings size={16} />
            <span>{t("common.sidebar.userMenu.settings")}</span>
          </UserMenuItem>
          <UserMenuItem onClick={() => {
            setShowReferralModal(true);
            setShowUserMenu(false);
          }}>
            <FiGift size={16} />
            <span>{t("common.sidebar.userMenu.referral")}</span>
          </UserMenuItem>
          {isPushSupported && (
            <UserMenuItem onClick={handleNotificationsToggle}>
              <FiBell size={16} />
              <span style={{ flex: 1 }}>{t("common.sidebar.userMenu.notifications")}</span>
              <ToggleSwitch $isOn={isPushSubscribed} />
            </UserMenuItem>
          )}
          <UserMenuItem onClick={logout} style={{ color: '#ef4444' }}>
            <FiLogOut size={16} />
            <span>{t("common.sidebar.userMenu.logout")}</span>
          </UserMenuItem>
        </UserMenu>,
        document.body
      )}

      {isCopilotActive && (
        <CopilotSubSidebar
          isVisible={isCopilotActive}
          onNewChat={handleNewChat}
        />
      )}

      {/* Modales para Usuarios y Suscripción */}
      {showUsersModal && (
        <UsersModal
          onClose={() => setShowUsersModal(false)}
          onOpenSubscription={() => {
            setShowUsersModal(false);
            eventBus.emit("OPEN_SUBSCRIPTION_MODAL");
          }}
        />
      )}

      {showReferralModal && <ReferralModal onClose={() => setShowReferralModal(false)} />}

      {showPdfSettingsModal && (
        <PdfSettingsModal onClose={() => setShowPdfSettingsModal(false)} />
      )}


    </>
  );
};
